var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { IconButton, OutlinedInput } from '@mui/material';
import AccurityIcon from '../../icon/components/AccurityIcon';
import { millisecondToISODate, moveCursorToTheEnd } from '../../common/utils';
import { useAccurityTimezone } from '../../timezoneProvider/AccurityTimezoneProvider';
import { useHover } from '../../common/hoverHook';
import { useOnEnterHandler } from '../hooks/onEnterHandlerHook';
import { hasCommentValidValue } from '../utils';
export var MAX_COMMENT_LENGTH = 1000;
export var CommentComponent = function (_a) {
    var comment = _a.comment, isMyComment = _a.isMyComment, onDelete = _a.onDelete, onSave = _a.onSave;
    var timezone = useAccurityTimezone();
    var _b = useHover(), textFieldRef = _b.hoverRef, isHovered = _b.isHovered, setIsHovered = _b.setIsHovered;
    var _c = useState(comment.text || ''), currentText = _c[0], setCurrentText = _c[1];
    var _d = useState(false), isRemoving = _d[0], setIsRemoving = _d[1];
    var _e = useState(false), isEditing = _e[0], setIsEditing = _e[1];
    useEffect(function () {
        setCurrentText(comment.text || '');
    }, [comment.text]);
    // move cursor to the end of string when user starts editing
    useEffect(function () {
        var _a;
        if (isEditing) {
            var textArea = (_a = textFieldRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('textarea');
            textArea && moveCursorToTheEnd(textArea);
        }
    }, [isEditing]);
    var isRemoved = !comment.text; // we don't delete the comment record, only the text is removed/set to empty value
    var deleteEditButtonsVisible = isMyComment && isHovered && !isRemoved && !isEditing && !isRemoving;
    var isEditInfoVisible = comment.version > 1 && !isRemoving && !isRemoved && !isEditing;
    var handleOnSave = function () {
        if (hasCommentValidValue(currentText) && comment.text !== currentText) {
            onSave(__assign(__assign({}, comment), { text: currentText })).then(function () {
                setIsEditing(false);
                setIsHovered(false);
            });
        }
        else {
            setIsEditing(false);
        }
    };
    var handleOnDelete = function () {
        onDelete(comment).then(function () {
            setIsRemoving(false);
            setIsHovered(false);
        });
    };
    var handleOnEditCancel = function () {
        setIsEditing(false);
        setCurrentText(comment.text || '');
    };
    var handleSaveOnEnter = useOnEnterHandler(handleOnSave);
    var getActionButtons = function () {
        if (deleteEditButtonsVisible) {
            return React.createElement(DeleteEditButtons, { onEdit: function () { return setIsEditing(true); }, onDelete: function () { return setIsRemoving(true); } });
        }
        else if (isRemoving) {
            return React.createElement(RemovalMessage, { onDelete: handleOnDelete, onCancel: function () { return setIsRemoving(false); } });
        }
        else if (isEditing) {
            return React.createElement(YesNoButtons, { onConfirm: handleOnSave, onReject: handleOnEditCancel, isConfirmDisabled: !hasCommentValidValue(currentText) });
        }
        return null;
    };
    return (React.createElement("div", { className: "comment" },
        React.createElement("div", { className: "commentAuthorInfo" },
            React.createElement("span", { className: "commentAuthorInfo__author" }, comment.createdBy.name),
            React.createElement("span", { className: "commentAuthorInfo__timestamp" }, millisecondToISODate(comment.createdDate, timezone))),
        React.createElement(OutlinedInput, { ref: textFieldRef, className: classNames('commentText', {
                'commentText--deleteEditButtonsVisible': deleteEditButtonsVisible,
                'commentText--removed': isRemoved,
                'commentText--editing': isEditing,
            }), margin: "dense", multiline: true, maxRows: 10, fullWidth: true, value: isRemoved ? getRemovedAtText(comment.changedBy, comment.changedDate, timezone) : currentText, disabled: !isEditing, endAdornment: getActionButtons(), onKeyDown: handleSaveOnEnter, onChange: function (e) { return setCurrentText(e.target.value); }, inputProps: { maxLength: MAX_COMMENT_LENGTH } }),
        isEditInfoVisible && React.createElement("div", { className: "commentEditInfo" },
            React.createElement("span", null, getEditInfo(comment.version, comment.changedBy, comment.changedDate, timezone)))));
};
var getRemovedAtText = function (changedBy, changedDate, timezone) {
    return "Removed at " + millisecondToISODate(changedDate, timezone) + " by " + changedBy.name;
};
var getEditInfo = function (version, changedBy, changedDate, timezone) {
    if (version <= 1) {
        return '';
    }
    else if (version === 2) {
        return "Edited at " + millisecondToISODate(changedDate, timezone) + " by " + changedBy.name;
    }
    else {
        return "Edited " + (version - 1) + " times, last time at " + millisecondToISODate(changedDate, timezone) + " by " + changedBy.name;
    }
};
var RemovalMessage = function (_a) {
    var onDelete = _a.onDelete, onCancel = _a.onCancel;
    return (React.createElement("div", { className: "commentRemovalMessage" },
        React.createElement("span", { className: "commentRemovalMessage__text" }, "Are you sure you want to remove this comment?"),
        React.createElement(YesNoButtons, { onConfirm: onDelete, onReject: onCancel })));
};
var DeleteEditButtons = function (_a) {
    var onDelete = _a.onDelete, onEdit = _a.onEdit;
    return (React.createElement("div", { className: 'commentActionButtons commentActionButtons--editDelete' },
        React.createElement(IconButton, { size: 'small', onClick: onDelete },
            React.createElement(AccurityIcon, { iconName: "mui:DeleteOutlined" })),
        React.createElement(IconButton, { size: 'small', onClick: onEdit },
            React.createElement(AccurityIcon, { iconName: "mui:EditOutlined" }))));
};
var YesNoButtons = function (_a) {
    var onReject = _a.onReject, onConfirm = _a.onConfirm, isConfirmDisabled = _a.isConfirmDisabled;
    return (React.createElement("div", { className: 'commentActionButtons' },
        React.createElement(IconButton, { size: 'small', onClick: onReject },
            React.createElement(AccurityIcon, { iconName: "mui:Close" })),
        React.createElement(IconButton, { size: 'small', onClick: onConfirm, disabled: isConfirmDisabled },
            React.createElement(AccurityIcon, { iconName: "mui:Check" }))));
};
//# sourceMappingURL=CommentComponent.js.map